import { z } from 'zod';
import { dataSchema, uuidSchema } from './comum.ts';
import {
  fatiaDaFaixaSchema,
  serieDeValoresSchema,
  totalDeTitulosSchema,
} from './painel-titulos.ts';

/**
 * Painel de contas a receber — o que se tem a receber, para quando, e de quem
 * (`.spec/010`, módulo Recebimento; `.spec/016`, seção 10).
 *
 * É **só leitura e só agregação**: nenhum título nasce, muda ou some por aqui. O
 * painel conta o que o contrato de recebimento e o lançamento digitado gravaram,
 * e o que as baixas abateram.
 *
 * **As faixas, a janela e o corte dos gráficos moram em `painel-titulos.ts`**,
 * compartilhados com o painel de contas a pagar: uma dívida e um crédito que
 * vencem em nove dias caem na mesma faixa, e ela se calcula da mesma forma. O
 * que sobrou neste arquivo é o que só vale para o lado que recebe.
 *
 * ## O que ele mede é o **saldo**, não o valor
 *
 * Todo número de "em aberto" desta tela sai de `valor_saldo`. A diferença
 * aparece no título recebido em parte: um de R$ 1.000,00 com R$ 700,00 já
 * recebidos tem R$ 300,00 a receber, e é R$ 300,00 que entra na previsão de
 * entrada da semana. Somar o valor original faria a projeção contar dinheiro que
 * já está no banco — e o erro é sempre para mais, que é o pior lado: aqui ele se
 * parece com caixa que não existe.
 *
 * ## As duas diferenças em relação ao painel de contas a pagar
 *
 * Elas não são de apresentação; vêm do modelo, e estão explicadas em
 * `db/schema/contas-receber.ts`:
 *
 * **1. A conta contábil é coluna do título, e não uma tabela de índices.** No
 * pagar, uma parcela de nota cobre itens de naturezas diferentes — cimento,
 * furadeira e serviço na mesma nota —, e por isso lá a classificação é rateada e
 * o gráfico é uma soma **ponderada**. Aqui o título carrega uma natureza de
 * receita só, então o gráfico é um `GROUP BY` direto. A consequência boa: o
 * total do gráfico bate com o indicador ao centavo, sem o arredondamento que o
 * outro lado precisa aparar.
 *
 * **2. A contraparte é o cliente, e ele aparece pela razão social.** O
 * fornecedor aparece pelo nome de uso — fantasia quando há — em todo o módulo de
 * pagamento; o cliente, pelo `nome`, que é o que a listagem de contas a receber
 * mostra. Um gráfico com o outro nome se leria como cliente duplicado.
 */

/**
 * O recorte do painel.
 *
 * Escrito aqui, e não em `painel-titulos.ts`, ainda que hoje seja idêntico ao do
 * pagar: a consulta é a fronteira pública desta rota, e o dia em que um dos dois
 * ganhar um filtro que o outro não tem — cliente aqui, fornecedor lá — a divisão
 * já está feita.
 *
 * Ausentes significam "tudo que o usuário alcança": o recorte por empresa
 * vinculada continua sendo do servidor, como em toda listagem.
 *
 * **A obra alcança o título pelo rateio**, que é o único caminho que existe. O
 * título não tem obra no cabeçalho, e quem sabe de que obra é a receita é
 * `contas_receber_rateio`. A consequência tem de ser dita na tela: com uma obra
 * escolhida, **o título sem rateio fica de fora**. Ele é a receita
 * administrativa, que não pertence a obra alguma, e atribuí-lo a todas faria o
 * mesmo dinheiro aparecer em cada recorte.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const painelContasReceberQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    obraId: uuidSchema.optional(),
  })
  .strict();

export type PainelContasReceberQuery = z.infer<typeof painelContasReceberQuerySchema>;

export const painelContasReceberSchema = z.object({
  /** Hoje pelo relógio do servidor — o corte entre o atraso e o resto. */
  hoje: dataSchema,
  /**
   * Tudo que tem saldo, **sem recorte de data**.
   *
   * É a soma exata das sete faixas — o servidor a calcula com `totalDasFaixas`,
   * sobre as mesmas linhas, em vez de perguntar de novo ao banco. Por
   * construção, então, o total fecha com os indicadores, e a tela pode afirmar
   * isso ao usuário.
   */
  emAberto: totalDeTitulosSchema,
  /** As sete faixas, na ordem do tempo, completas. */
  porFaixa: z.array(fatiaDaFaixaSchema),
  /** Primeiro dia da janela das recebidas. */
  recebidasDe: dataSchema,
  /** Último dia da janela das recebidas — hoje. */
  recebidasAte: dataSchema,
  /** As baixas não estornadas da janela: quantas e quanto de principal. */
  recebidas: totalDeTitulosSchema,
  emAbertoPorContaContabil: serieDeValoresSchema,
  emAbertoPorCliente: serieDeValoresSchema,
  recebidasPorContaContabil: serieDeValoresSchema,
  recebidasPorCliente: serieDeValoresSchema,
});

export type PainelContasReceber = z.infer<typeof painelContasReceberSchema>;
